import isEmpty from "lodash/isEmpty";
import isEqual from "lodash/isEqual";
import { useEffect } from "react";
import { useDispatch } from "react-redux";

import {
    MapTheme,
    SavedViewExtended,
    useFetchSavedViewExtendedQuery,
} from "@biggeo/bg-server-lib/studio";
import { WithRequiredProperty } from "@biggeo/bg-utils";
import { useMap } from "../mapbox/context";
import { useMapData } from "../redux/hooks";
import { mapDataActions } from "../redux/model";
import { setCustomShapeStyling } from "../utils/style-utils";
import { FunctionType } from "../utils/type";
import { getSavedViewPolygons } from "../utils/utils";
import MapViewPage, { IMapViewPage } from "./MapViewPage";
import { IMapViewWrapper, MapTableTabsType } from "./MapViewWrapper";
import { usePolygon, useMapStateUtilities } from "../mapbox/context/hooks";

interface ISavedViewPage
    extends WithRequiredProperty<IMapViewWrapper, "datasets" | "mapTemplateId">,
        MapTableTabsType,
        IMapViewPage {
    readonly savedViewId: string;
}

const SavedViewPage = ({
    savedViewId,
    mapTemplateId,
    setSearchParams,
    tab,
    setTab,
    openGenerateFilePage,
    setOpenGenerateFilePage,
    ...props
}: ISavedViewPage) => {
    const dispatch = useDispatch();
    const savedView = useMapData();

    const { overridePolygons } = usePolygon();

    const {
        map,
        draw,
        isMapLoaded,
        datasets,
        dispatch: mapDispatch,
        mapStyle,
    } = useMap();

    const { setFunctionType, setViewport } = useMapStateUtilities();

    // We fetch without datasets, since they were already fetched and set in state via useSavedViewDatasets in FullMapContainer.
    const {
        queryReturn: { data: _ },
    } = useFetchSavedViewExtendedQuery({
        variables: { id: savedViewId, withDatasets: false },
        notifyOnNetworkStatusChange: true,
        onCompleted: ({ fetchSavedViewExtended: data }) => {
            dispatch(mapDataActions.updateMapData(data));

            // Style needs to be set first for the map to load
            const style = data.savedView.theme
                ? data.savedView.theme
                : MapTheme.light;

            if (!isEqual(mapStyle, style)) {
                mapDispatch?.({
                    type: "SET_MAP_STYLE",
                    values: style,
                });
            }
        },
    });

    const setSavedView = ({
        savedView,
        savedArea,
    }: Pick<SavedViewExtended, "savedView" | "savedArea">) => {
        if (savedArea && !isEmpty(savedArea.geometries)) {
            const { drawnPolygons, boundaryPolygons } =
                getSavedViewPolygons(savedArea);

            overridePolygons([...drawnPolygons, ...boundaryPolygons]);

            if (drawnPolygons.length > 0) {
                if (draw.current) {
                    draw.current.add({
                        type: "FeatureCollection",
                        features: drawnPolygons.map((p) => p.feature),
                    });

                    // A timeout to give time to the map to set the layers of the selected datasets
                    setTimeout(() => {
                        if (map.current) {
                            setCustomShapeStyling({
                                map: map.current,
                                isMapLoaded,
                                datasets,
                                geometries: drawnPolygons.map((p) => p.feature),
                            });
                        }
                    }, 500);
                }
            }

            if (boundaryPolygons.length > 0) {
                setFunctionType(FunctionType.savedPolygon);
            }
        }

        if (savedView.viewport) {
            // Note: The SV viewport bounds are now set in hooks.ts when the map is initialized
            if (!savedArea || isEmpty(savedArea.geometries)) {
                setViewport(savedView.viewport);
            }
        }
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    useEffect(() => {
        if (savedView && isMapLoaded && map.current) {
            setSavedView({
                savedArea: savedView.savedArea || undefined,
                savedView: savedView.savedView,
            });
        }
    }, [savedView, isMapLoaded, map.current, draw.current, datasets.length]);

    return (
        <MapViewPage
            {...props}
            mapTemplateId={mapTemplateId}
            savedViewId={savedViewId}
            setSearchParams={setSearchParams}
            tab={tab}
            setTab={(t) => {
                setTab(t);
            }}
            openGenerateFilePage={openGenerateFilePage}
            setOpenGenerateFilePage={setOpenGenerateFilePage}
        />
    );
};

export default SavedViewPage;
